import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CollaborationWorkList } from '~/data/collaboration';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { updateEmployeeTemplateTasks } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/data';
import {
  WorklistFormData,
  convertEditWorklistDataToSubmitPayload,
} from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistDialog/data';
import { useWorklistValidation } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistDialog/useWorklistValidation';

export type UseEditWorklistDialogProps = {
  worklistTemplates: CollaborationWorkList[];
  employeeId: number;
  selectedWorklist: WorklistFormData;
  onClose: () => void;
  onProcessSubmission?: (data: WorklistFormData) => Promise<void>;
  onUpdate?: () => void;
};

export const useEditWorklistDialog = (props: UseEditWorklistDialogProps) => {
  const { onClose, selectedWorklist, employeeId, onUpdate, worklistTemplates } =
    props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { getEnums } = useAllEnums();
  const { taskTypeEnums } = useMemo(() => {
    return {
      taskTypeEnums: getEnums(EnumListKey.COLLAB_TASK_TYPE),
    };
  }, [getEnums]);
  const getTaskTypeName = useCallback(
    (typeCode: string | undefined): string | undefined =>
      taskTypeEnums.find((e) => e.code === typeCode)?.name,
    [taskTypeEnums],
  );

  const { validationSchema } = useWorklistValidation();

  const resolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  const form = useForm<WorklistFormData>({
    resolver,
    defaultValues: selectedWorklist,
  });
  const { reset, handleSubmit, watch } = form;

  const [dueDateMin, startDateMax] = watch(['startDate', 'dueDate']);

  const onCloseDialog = useCallback(() => {
    reset(selectedWorklist);
    onClose();
  }, [reset, selectedWorklist, onClose]);

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const payload = convertEditWorklistDataToSubmitPayload(data);

      return updateEmployeeTemplateTasks(employeeId, payload)
        .then(() => {
          enqueueSnackbar(t('listUpdated'), {
            variant: 'success',
          });
          if (!!onUpdate) {
            onUpdate();
            onCloseDialog();
          }
        })
        .catch((err) => {
          enqueueSnackbar(t('failedToUpdate'), {
            variant: 'error',
          });
          console.log(err);
        });
    });
  }, [handleSubmit, employeeId, onUpdate, onCloseDialog, enqueueSnackbar, t]);

  const worklistTemplate = useMemo(() => {
    return worklistTemplates.find(
      (wt) => wt.fCollaborationTemplateId === selectedWorklist.templateId,
    );
  }, [worklistTemplates, selectedWorklist]);

  return {
    form,
    onCloseDialog,
    onSubmit,
    worklistTemplate,
    getTaskTypeName,
    dateLimits: {
      dueDateMin,
      startDateMax,
    },
  };
};
